import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './DocumentMediaRemarks.scss';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import {isEmpty} from 'lodash';
import {
  IDocumentMediaAddEditRemarkRequest,
  IDocumentMediaRemark,
} from '../../../../../../api/types/document.type';
import {useDocumentMediaAddEditRemarks} from '../../../../../../hooks/document/useDocumentMediaRemarks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {convertToUTCDate} from '../../../../../../utils/formatters';
import {DocMediaTimePicker} from '../media-redactions/DocMediaTimePicker';
import ArrToastNotification, {
  ArrStatusToastProps,
} from '../../../../../common/arr-toast-notification/ArrToastNotification';
import ReactDOM from 'react-dom';
import {COLOR_MAP, ToastType} from '../../../../../../shared/constants';
import {
  isTimeNotMoreThan,
  msToTime,
  validateTimeString,
} from '../../../../../../utils/utils';
import ArrModal from '../../../../../common/arr-modal/ArrModal';

dayjs.extend(utc);

export type RemarkAction = 'add' | 'edit' | 'delete';

interface DocumentMediaRemarkProps {
  userName?: string;
  documentId?: string;
  annotationLayerId?: string;
  documentGUID?: string;
  ingestionGUID?: string;
  miniMediaLength?: string;
  viewRemark?: IDocumentMediaRemark;
  onSavedRemark?: (action?: RemarkAction) => void;
  onCancelRemark?: () => void;
}

const DocumentMediaRemark: React.FC<DocumentMediaRemarkProps> = ({
  userName,
  documentId,
  annotationLayerId,
  documentGUID,
  ingestionGUID,
  miniMediaLength,
  viewRemark,
  onSavedRemark,
  onCancelRemark,
}: DocumentMediaRemarkProps) => {
  const [editRemark, setEditRemark] = useState<boolean>();
  const [remarkText, setRemarkText] = useState<string>(
    viewRemark?.remarkText ?? ''
  );
  const [remarkTime, setRemarkTime] = useState<string>(
    viewRemark?.startTime ?? '0:00:00'
  );
  const [showWarning, setShowWarning] = useState<boolean>();
  const messageRef = useRef<string>('');

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const {
    mutate: addEditRemark,
    isSuccess,
    isPending,
    reset,
  } = useDocumentMediaAddEditRemarks();

  const isAdd = useMemo(() => isEmpty(viewRemark), [viewRemark]);

  const isDisabledAddEditRemark = useMemo(() => {
    return !(validateTimeString(remarkTime) && !isEmpty(remarkText));
  }, [remarkText, remarkTime]);

  useEffect(() => {
    if (isSuccess) {
      reset();
      onSavedRemark?.(showDeleteModal ? 'delete' : 'edit');
      setEditRemark(false);
      setShowDeleteModal(false);
    }
  }, [editRemark, isSuccess, onSavedRemark, reset, showDeleteModal]);

  const onDeleteRemark = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const onEditRemark = useCallback(() => {
    setEditRemark(true);
  }, []);

  const onSaveRemark = useCallback(() => {
    const isMoreThan = isTimeNotMoreThan(msToTime(miniMediaLength), remarkTime);

    if (isMoreThan) {
      messageRef.current = t('docRemarks.remarkTimeCantBeSet');
      setShowWarning(true);
    } else {
      const requestParams: IDocumentMediaAddEditRemarkRequest = {
        documentID: Number.parseInt(documentId ?? '0'),
        annotationLayerID: Number.parseInt(annotationLayerId ?? '0'),
        documentGUID,
        ingestionGUID,
        userName,
        isDelete: false,
        startTime: remarkTime,
        remarkText: remarkText,
        actionType: isAdd ? 'add' : 'edit',
        leftMargin: '1',
        createdDate: dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      };
      if (editRemark) {
        requestParams.documentAudioRemarkID = viewRemark?.documentAudioRemarkID;
      }
      addEditRemark(requestParams);
    }
  }, [
    remarkTime,
    miniMediaLength,
    documentId,
    annotationLayerId,
    documentGUID,
    ingestionGUID,
    userName,
    remarkText,
    isAdd,
    editRemark,
    addEditRemark,
    viewRemark?.documentAudioRemarkID,
  ]);

  const deleteRemark = useCallback(() => {
    const requestParams = {
      documentAudioRemarkID: viewRemark?.documentAudioRemarkID,
      documentID: Number.parseInt(documentId ?? '0'),
      isDelete: true,
      actionType: 'delete',
      documentGUID,
      ingestionGUID,
      annotationLayerID: Number.parseInt(annotationLayerId ?? '0'),
      userName,
      startTime: viewRemark?.startTime ?? '',
      leftMargin: viewRemark?.leftMargin ?? '',
      remarkText: viewRemark?.remarkText ?? '',
    } as unknown as IDocumentMediaAddEditRemarkRequest;
    addEditRemark(requestParams);
  }, [
    addEditRemark,
    annotationLayerId,
    documentGUID,
    documentId,
    ingestionGUID,
    userName,
    viewRemark?.documentAudioRemarkID,
    viewRemark?.leftMargin,
    viewRemark?.remarkText,
    viewRemark?.startTime,
  ]);

  const onCancelEditRemark = useCallback(() => {
    setEditRemark(false);
    onCancelRemark?.();
  }, [onCancelRemark]);

  const onChangeRemarkTime = useCallback((value: string) => {
    setRemarkTime(value);
  }, []);

  const onClosedToast = useCallback(() => {
    messageRef.current = '';
    setShowWarning(false);
  }, []);

  const ErrorToast = useCallback((props: ArrStatusToastProps) => {
    return ReactDOM.createPortal(
      <ArrToastNotification {...props} />,
      document.body
    );
  }, []);

  const renderViewRemarkComponent = useCallback(() => {
    return (
      <Box className={'doc-remark'}>
        <Box
          className={'doc-remark-color'}
          sx={{backgroundColor: viewRemark?.remarkColor}}
        />
        <Box className={'doc-remark-info'}>
          <Typography
            className="typo-text body5_size doc-text-color doc-text-date"
            data-testid={`document-media-remark-created-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
          >
            {convertToUTCDate(`${viewRemark?.createdDate}Z`)}
          </Typography>
          <Box className={'doc-remark--sub-info'}>
            <Typography
              className="typo-text label3_size doc-text-color"
              data-testid={`document-media-remark-username-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
            >
              {viewRemark?.userName}
            </Typography>
            <Typography
              className="typo-text body4_size doc-text-color doc-text-time"
              data-testid={`document-media-remark-starttime-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
            >
              {viewRemark?.startTime}
            </Typography>
            <Typography
              className="typo-text body4_size doc-text-color"
              data-testid={`document-media-remark-remarkText--${viewRemark?.documentAudioRemarkID ?? 'new'}`}
            >
              {viewRemark?.remarkText}
            </Typography>
            <Box className={'remark-action'}>
              <ArrButton
                dataTestId={`delete-remark-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
                variant={'text'}
                size={'small'}
                onClick={onDeleteRemark}
                aria-label={t('common.delete')}
              >
                {t('common.delete')}
              </ArrButton>
              <ArrButton
                dataTestId={`edit-remark-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
                variant={'text'}
                size={'small'}
                onClick={onEditRemark}
                aria-label={t('common.edit')}
              >
                {t('common.edit')}
              </ArrButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }, [
    viewRemark?.createdDate,
    viewRemark?.remarkColor,
    viewRemark?.remarkText,
    viewRemark?.startTime,
    viewRemark?.userName,
    onDeleteRemark,
    onEditRemark,
  ]);

  const renderAddEditRemarkComponent = useMemo(() => {
    return (
      <Box className={'doc-remark'}>
        <Box
          className={'doc-remark-color'}
          sx={{
            backgroundColor: isAdd
              ? COLOR_MAP['Green']
              : viewRemark?.remarkColor,
          }}
        />
        <Box className={'doc-remark-info'}>
          <Typography
            className="typo-text body5_size doc-text-color doc-text-date"
            data-testid={`document-media-remark-typography-icon-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
          >
            {isAdd
              ? dayjs().format('YYYY/MM/DD')
              : convertToUTCDate(`${viewRemark?.createdDate}Z`)}
          </Typography>
          <Box className={'doc-remark-sub-info'}>
            <Typography
              className="typo-text label3_size doc-text-color"
              data-testid={`document-media-remark-typography-name-user--${viewRemark?.documentAudioRemarkID}?? "new"`}
            >
              {isAdd ? userName : viewRemark?.userName}
            </Typography>
            <DocMediaTimePicker
              initialValue={remarkTime}
              onChange={onChangeRemarkTime}
            />
            <ArrTextArea
              value={remarkText}
              rows={2}
              maxLength={1000}
              onChange={setRemarkText}
              dataTestId={`remark-text-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
            />
            <Box className={'add-edit-remark-action'}>
              <ArrButton
                dataTestId={`cancel-remark-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
                variant={'text'}
                size={'small'}
                onClick={onCancelEditRemark}
                aria-label={t('common.cancel')}
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                dataTestId={`add-edit-remark-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
                variant={'text'}
                size={'small'}
                className={
                  isDisabledAddEditRemark ? 'button-disabled' : undefined
                }
                disabled={isDisabledAddEditRemark}
                onClick={onSaveRemark}
                aria-label={
                  isAdd ? t('docRemarks.addRemark') : t('docRemarks.save')
                }
              >
                {isAdd ? t('docRemarks.addRemark') : t('docRemarks.save')}
              </ArrButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }, [
    isAdd,
    viewRemark?.remarkColor,
    viewRemark?.createdDate,
    viewRemark?.userName,
    userName,
    remarkTime,
    onChangeRemarkTime,
    remarkText,
    onCancelEditRemark,
    isDisabledAddEditRemark,
    onSaveRemark,
  ]);

  return (
    <div
      data-testid={`doc-remark-${viewRemark?.documentAudioRemarkID ?? 'new'}`}
    >
      {isAdd || editRemark
        ? renderAddEditRemarkComponent
        : renderViewRemarkComponent()}
      {showWarning && (
        <ErrorToast
          message={messageRef.current}
          onClose={onClosedToast}
          status={[ToastType.Error]}
        />
      )}
      <ArrModal
        showModal={showDeleteModal}
        ariaLabel={t('docRemarks.delete.title')}
        title={t('docRemarks.delete.title')}
        ariaDescription={t('docRemarks.delete.description')}
        description={t('docRemarks.delete.description')}
        onClose={() => setShowDeleteModal(false)}
        autoFocusCloseButton={true}
        dataTestId="document-media-modal"
        buttons={[
          {
            id: 'no-button',
            label: t('common.no'),
            dataTestId: 'dmm-no-button',
            onClick: () => setShowDeleteModal(false),
            variant: 'secondary',
            disabled: isPending,
          },
          {
            id: 'yes-button',
            label: t('common.yes'),
            dataTestId: 'dmm-yes-button',
            onClick: deleteRemark,
            variant: 'primary',
            loading: isPending,
            disabled: isPending,
          },
        ]}
      />
    </div>
  );
};

export {DocumentMediaRemark};
